import { FC, useEffect, useState } from 'react';
import { InlineMessages, InlineMessagesType } from '@marriott/mi-ui-library';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { useQuery } from '@apollo/client';
import { recordRetargetingCandidate } from '@marriott/mi-localstorage-utils';
import {
  getReslinkEventByIdDttQuery,
  HotelMediaContent,
  GuestReslinkEventByIdDttResponse,
  ReslinkEventByIdInput,
  ReslinkType,
  ImageType,
  HotelGroupEventDetails,
} from '@marriott/mi-groups-graphql';
import { useLocaleStore, useReslinkGuestStore } from '../../store';
import {
  isAllowedForLocale,
  getQueryParams,
  setCalendarOptions,
  updateDataLayerWithInventoryAttributes,
  getSession,
} from '../../utils';
import { useIdleTimer } from '../../hooks/useIdleTimer';
import { formatEventDate, isPastDate, isToday } from '../../utils/date';
import { ViewType } from '../SearchResults/SearchResults.types';
import { InventoryInfo, ViewToggle } from '../../molecules';
import { PROPERTY_IMAGE_LIST_CATEGORY_LIMIT, HTTP_SUCCESS_CODE, ASSET_DOMAIN } from '../../constants';
import { ReslinkGuestProps } from './ReslinkGuest.types';
import { PropertyMap } from './PropertyMap';
import { EventBanner } from './EventBanner';
import { PropertyList } from './PropertyList';
import { StyledPropertyList } from './ReslinkGuest.styles';
import { SessionTimeoutModal } from './SessionTimeoutModal';

export const ReslinkGuestDttConfig = {
  emptyLabel: 'ReslinkGuest',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/reslink/reslinkguest`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ReslinkGuestDtt: FC<{ model: ReslinkGuestProps }> = (props: any) => {
  const {
    carouselAriaLabel,
    carouselImageAriaLabel,
    logoAriaLabel,
    bannerImageAltText,
    externalLinkAriaLabel,
    viewEventDetails,
    hideEventDetails,
    viewLessDetails,
    viewMoreDetails,
    bookBy,
    toAirport,
    reviews,
    reviewTooltipText,
    freeWifi,
    viewHotelDetails,
    priceType,
    dateLabels,
    roomsAndGuests,
    checkAvailability,
    trackingProperties,
    hotelDetails: hotelDetailsLabel,
    overview,
    amenities,
    map,
    mapAriaLabel,
    errors,
    inventoryLabels,
    sessionTimeout,
  } = props.model;

  const { shortWeekdayNames, longWeekdayNames, shortMonthNames, longMonthNames } = dateLabels;
  const {
    heading,
    description,
    continue: continueText,
    continueAriaLabel,
    sessionTrackLocation,
    sessionTrackDescription,
  } = sessionTimeout || {};

  const { locale } = useLocaleStore();
  const { isReslinkBlockFull, isMissingInventory } = useReslinkGuestStore();

  const [eventId, setEventId] = useState('');
  const [reslinkType, setReslinkType] = useState<ReslinkType>(ReslinkType.NONE);
  const [showMapView, setShowMapView] = useState(false);
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);

  useIdleTimer(() => setShowTimeoutModal(true));

  useEffect(() => {
    const fetchSession = async () => {
      try {
        await getSession();
      } catch (error) {
        console.error(error);
      }
    };
    fetchSession();
  }, []);

  useEffect(() => {
    setCalendarOptions({
      shortWeekdayNames,
      longWeekdayNames,
      shortMonthNames,
      longMonthNames,
    });
  }, [shortWeekdayNames, longWeekdayNames, shortMonthNames, longMonthNames]);

  useEffect(() => {
    const { id, key } = getQueryParams(window.location.search);
    setEventId(id);
    setReslinkType(ReslinkType[key as keyof typeof ReslinkType] ?? ReslinkType.NONE);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const shouldRemoveInventoryMissing = !isMissingInventory && params.has('inventoryMissing');
    const shouldRemoveBlockFull = !isReslinkBlockFull && params.has('blockFull');

    if (shouldRemoveInventoryMissing || shouldRemoveBlockFull) {
      if (shouldRemoveInventoryMissing) params.delete('inventoryMissing');
      if (shouldRemoveBlockFull) params.delete('blockFull');

      const updatedQuery = params.toString();
      window.history.replaceState({}, '', `?${updatedQuery}`);
      updateDataLayerWithInventoryAttributes(updatedQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reslinkEventInput: ReslinkEventByIdInput = {
    input: { eventId, reslinkType },
    groupReslink: reslinkType === ReslinkType.GRP,
    imageLimitPerCategory: PROPERTY_IMAGE_LIST_CATEGORY_LIMIT,
  };

  const {
    data: eventData,
    loading,
    error,
  } = useQuery<GuestReslinkEventByIdDttResponse>(getReslinkEventByIdDttQuery, {
    variables: reslinkEventInput,
    context: {
      headers: {
        'accept-language': locale,
        'uxl-cache-disable': true,
      },
    },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    skip: !eventId,
  });
  const { reslinkEventByIdWithInventory } = eventData?.sales?.groupSales || {
    sales: { groupSales: { reslinkEventByIdWithInventory: {} } },
  };
  const {
    eventName,
    eventDescription,
    eventCustomLinks,
    customImageURLs,
    hotelGroupEventDetails,
    startDate,
    endDate,
    httpStatus,
  } = reslinkEventByIdWithInventory || {};

  const reslinkEventCustomLinks = eventCustomLinks?.filter(link => link.name !== '__branchIoType');

  const eventStartDate = formatEventDate(startDate);
  const eventEndDate = formatEventDate(endDate);

  const eventInventoryStartDate = formatEventDate(startDate, 'slashedDateWithMonthNoAndYear');
  const eventInventoryEndDate = formatEventDate(endDate, 'slashedDateWithMonthNoAndYear');

  const filteredHotelDetails =
    reslinkType === ReslinkType.GRP
      ? hotelGroupEventDetails?.filter((property: HotelGroupEventDetails) => {
          const timespan = property?.groupEventDetails?.groupBlock?.groupContract?.timespans?.[0];
          const hasValidGroupBlock = !!property.groupEventDetails.groupBlock;

          const isStartPast = isPastDate(property?.overriddenEventStartDate || timespan?.start);
          const isEndPast = isPastDate(property?.overriddenEventEndDate || timespan?.end);

          const isEndDateToday = isToday(
            property?.overriddenEventEndDate || timespan?.end,
            'hyphenatedDateWithMonthNoAndYear'
          );
          return hasValidGroupBlock && !isEndDateToday && !(isStartPast && isEndPast);
        })
      : hotelGroupEventDetails;
  const allPropertiesExpired = reslinkType === ReslinkType.GRP && !filteredHotelDetails?.length;

  useEffect(() => {
    if (!eventData || !reslinkEventByIdWithInventory) return;

    const firstPropertyInEvent = filteredHotelDetails?.[0];
    const rawImageUrl =
      firstPropertyInEvent?.property?.media?.primaryImage?.edges?.[0]?.node?.imageUrls?.wideHorizontal ?? '';
    const imageUrl =
      rawImageUrl && (rawImageUrl.startsWith('http://') || rawImageUrl.startsWith('https://'))
        ? rawImageUrl
        : `${ASSET_DOMAIN}${rawImageUrl}`;
    const marshaCode = hotelGroupEventDetails?.map(p => String(p.property.id)) ?? [];
    const groupName = firstPropertyInEvent?.property?.basicInformation?.name ?? undefined;
    const eventExpirationDate = endDate ?? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
    const cancellationFlag = !reslinkEventByIdWithInventory.isActive || allPropertiesExpired;

    recordRetargetingCandidate({
      useCase: 'reslink',
      displayName: eventName ?? '',
      imageUrl,
      targetUrl: window.location.href,
      marshaCode,
      groupName,
      eventExpirationDate,
      cancellationFlag,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventData]);

  const slicedImages = showMapView ? customImageURLs?.slice(0, 1) : customImageURLs;
  const transformMedia = {
    photoGallery: {
      hotelView: {
        edges: slicedImages?.map((item: ImageType, index: number) => ({
          node: {
            alternateDescription: `${bannerImageAltText} ${index + 1}`,
            imageUrls: {
              wideHorizontal: `${item.link}`,
              classicHorizontal: `${item.link}`,
              square: `${item.link}`,
            },
          },
        })),
      },
    },
  };

  const toggleView = () => {
    setShowMapView(!showMapView);
    window.scrollTo(0, 0);
  };

  return (
    <EditableComponent config={ReslinkGuestDttConfig} {...props}>
      {loading ? (
        <div className="m-spinner-m d-block mx-auto my-4"></div>
      ) : (error && !eventData) || (eventData && httpStatus && httpStatus !== HTTP_SUCCESS_CODE) ? (
        <div className="py-4">
          <InlineMessages
            type={InlineMessagesType.Error}
            title={errors.apiFailure.title}
            messageList={[errors.apiFailure.description]}
          />
        </div>
      ) : eventData && !reslinkEventByIdWithInventory?.isActive ? (
        <div className="py-4">
          <div className="t-title-m mb-2">{errors.disabled.title}</div>
          <div>{errors.disabled.description}</div>
        </div>
      ) : allPropertiesExpired ? (
        <div className="py-4">
          <div className="t-title-m mb-2">{errors?.expiredProperties?.title}</div>
          <div>{errors?.expiredProperties?.description}</div>
        </div>
      ) : (
        <div className="m-container-fullbleed">
          {eventName ? (
            <EventBanner
              eventTitle={eventName}
              eventDescription={eventDescription as string}
              customLinks={reslinkEventCustomLinks}
              externalLinkAriaLabel={externalLinkAriaLabel}
              isMapView={showMapView}
              eventStartDate={eventStartDate}
              eventEndDate={eventEndDate}
              eventBannerLabels={{ viewEventDetails, hideEventDetails, viewLessDetails, viewMoreDetails }}
              {...(customImageURLs?.length ? { media: transformMedia as HotelMediaContent } : null)}
            />
          ) : null}
          <StyledPropertyList>
            {filteredHotelDetails?.length ? (
              <div className="container">
                {showMapView ? (
                  <PropertyMap
                    reslinkType={reslinkType}
                    propertyLabels={props.model}
                    properties={filteredHotelDetails}
                    toggleView={toggleView}
                  />
                ) : (
                  <>
                    {isMissingInventory || isReslinkBlockFull ? (
                      <div className="pt-3">
                        <InventoryInfo
                          description={inventoryLabels?.description}
                          startDate={eventInventoryStartDate}
                          endDate={eventInventoryEndDate}
                        />
                      </div>
                    ) : null}
                    <PropertyList
                      reslinkType={reslinkType}
                      isMapView={showMapView}
                      properties={filteredHotelDetails}
                      propertyLabels={{
                        carouselAriaLabel,
                        carouselImageAriaLabel,
                        logoAriaLabel,
                        bookBy,
                        toAirport,
                        reviews,
                        reviewTooltipText,
                        freeWifi,
                        viewHotelDetails,
                        priceType,
                        dateLabels,
                        roomsAndGuests,
                        checkAvailability,
                        trackingProperties,
                        hotelDetails: hotelDetailsLabel,
                        overview,
                        amenities,
                        mapAriaLabel,
                        inventoryLabels,
                        apiError: errors.apiFailure.title,
                      }}
                    />
                    {isAllowedForLocale(locale) && (
                      <ViewToggle
                        ctaLabel={map}
                        variant={ViewType.MAP_VIEW}
                        ctaAriaLabel={mapAriaLabel}
                        toggleView={toggleView}
                      />
                    )}
                  </>
                )}
              </div>
            ) : null}
          </StyledPropertyList>
        </div>
      )}
      {sessionTimeout && (
        <SessionTimeoutModal
          show={showTimeoutModal}
          onClose={() => window.location.reload()}
          heading={heading}
          description={description}
          continue={continueText}
          continueAriaLabel={continueAriaLabel}
          sessionTrackLocation={sessionTrackLocation}
          sessionTrackDescription={sessionTrackDescription}
        />
      )}
    </EditableComponent>
  );
};
